'use client';

import { FedExIcon, USPSIcon } from '@c/icons';
import { UPS } from '@c/icons/carriers';
import Button from '@ui/Button';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import ItemShippingSelect from 'features/checkout/components/ItemShippingSelect';
import { AddressDocument } from 'models/address';
import { ProductDocument } from 'models/product';
import { useState } from 'react';
import type { Rate } from '@util/firestore/shipengine';

interface OfferShippingOptionsProps {
  product: ProductDocument;
  shipTo?: AddressDocument;
  offer_amount: number;
  selectedRate: Rate | null;
  setSelectedRate: (rate: Rate | null) => void;
  ratesError: Error | null;
  setRatesError: (error: Error | null) => void;
}

export default function OfferShippingOptions({
  product,
  shipTo,
  offer_amount,
  selectedRate,
  setSelectedRate,
  ratesError,
  setRatesError,
}: OfferShippingOptionsProps) {
  const [showShippingOptions, setShowShippingOptions] = useState(false);

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <span className="font-semibold">Selected Shipping</span>
      {selectedRate ? (
        <>
          <div className="flex w-full gap-[1.6rem]">
            {selectedRate.carrier_code === 'fedex' ? (
              <FedExIcon />
            ) : selectedRate.carrier_code === 'ups' ? (
              <UPS />
            ) : (
              <USPSIcon />
            )}
            <div className="flex grow items-center gap-5">
              <div>
                <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                  {selectedRate.delivery_days} Days
                </h5>
                <p className="text-[1.4rem] text-brand-gray">
                  {selectedRate.service_type}
                </p>
              </div>
              <div className="ml-auto text-[1.6rem] font-semibold">
                {formatCurrency(selectedRate.total_amount)}
              </div>
            </div>
          </div>
          <Button
            type="text"
            onClick={() => setShowShippingOptions((s) => !s)}
            text={
              showShippingOptions
                ? 'Hide Shipping Options'
                : 'Show More Shipping Options'
            }
          />
        </>
      ) : !ratesError ? (
        <div className="flex w-full flex-col gap-4">
          <span className="animate-pulse text-[1.4rem] font-semibold">
            Loading shipping options...
          </span>
          <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
          <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
          <div className="w-full animate-pulse rounded-[.4rem] bg-brand-lightest-gray p-4" />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-4">
          <span className="text-[1.4rem] font-semibold text-brand-red">
            {ratesError.message}
          </span>
        </div>
      )}
      {shipTo && (
        <div className="max-h-[35rem] overflow-y-auto">
          <ItemShippingSelect
            chooseCheapestByDefault
            hideProduct
            product={product}
            shipment={product.shipment}
            shipTo={shipTo}
            hide={!showShippingOptions}
            offer_amount={offer_amount}
            selectedRateId={selectedRate?.rate_id ?? null}
            onOptionSelected={({ rate }) => {
              setSelectedRate(rate);
            }}
            onError={(error) => {
              logError('Error fetching shipping rates', error.message);
              setRatesError(error);
            }}
          />
        </div>
      )}
    </div>
  );
}
